import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSpinner, 
    faImage, 
    faVideo, 
    faFile, 
    faTrash, 
    faEdit, 
    faEye, 
    faDownload,
    faSearch,
    faFilter,
    faCalendarAlt,
    faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import api from '../utils/api';
import PropTypes from 'prop-types';

const MediaManagement = ({ darkMode, onEditMedia }) => {
    const [media, setMedia] = useState([]);
    const [filteredMedia, setFilteredMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('All');
    const [showFilters, setShowFilters] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        show: false,
        mediaId: null,
        mediaTitle: '',
        isDeleting: false
    });

    const mediaTypes = ['All', 'image', 'video', 'document'];

    useEffect(() => {
        fetchMedia();
    }, []);

    useEffect(() => {
        filterMedia();
    }, [media, searchTerm, selectedType]);

    const fetchMedia = async () => {
        try {
            setLoading(true);
            const response = await api.get('/media/all');
            
            if (response.data.success) {
                setMedia(response.data.media);
            } else {
                setMedia([]);
            }
        } catch (error) {
            console.error('Error fetching media:', error);
            toast.error('Failed to load media');
            setMedia([]);
        } finally {
            setLoading(false);
        }
    };

    const filterMedia = () => {
        let filtered = [...media];

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(item =>
                item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // Filter by type
        if (selectedType !== 'All') {
            filtered = filtered.filter(item => item.mediaType === selectedType);
        }

        // Sort by newest first
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setFilteredMedia(filtered);
    };

    const handleDeleteMedia = (mediaId) => {
        const mediaItem = media.find(m => m._id === mediaId);
        setDeleteConfirmation({
            show: true,
            mediaId,
            mediaTitle: mediaItem.title,
            isDeleting: false
        });
    };

    const confirmDelete = async () => {
        setDeleteConfirmation(prev => ({ ...prev, isDeleting: true }));
        try {
            await api.delete(`/media/${deleteConfirmation.mediaId}`);
            setMedia(prev => prev.filter(item => item._id !== deleteConfirmation.mediaId));
            setDeleteConfirmation({ show: false, mediaId: null, mediaTitle: '', isDeleting: false });
            toast.success('Media deleted successfully!');
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete media');
            setDeleteConfirmation(prev => ({ ...prev, isDeleting: false }));
        }
    };

    const handleDownload = (mediaUrl, title) => {
        const link = document.createElement('a');
        link.href = mediaUrl;
        link.download = title;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'Unknown size';
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getMediaIcon = (type) => {
        switch (type) {
            case 'image': return faImage;
            case 'video': return faVideo;
            default: return faFile;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'image':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'video':
                return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
            case 'document':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Media Management
                </h2>
            </div>

            {/* Content Container */}
            <div className={`rounded-2xl border p-6 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } shadow-lg`}>
                <div className="space-y-6">
                    {/* Search and Filters */}
                    <div className="mb-6 flex flex-col lg:flex-row gap-4 items-center justify-between">
                        {/* Search */}
                        <div className="relative flex-1 max-w-md">
                            <FontAwesomeIcon 
                                icon={faSearch} 
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                            />
                            <input
                                type="text"
                                placeholder="Search media..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                                    darkMode 
                                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                        : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                            />
                        </div>

                        {/* Filter Toggle */}
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                                darkMode 
                                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                                    : 'bg-white hover:bg-gray-50 text-gray-800 border border-gray-300'
                            }`}
                        >
                            <FontAwesomeIcon icon={faFilter} />
                            Filters
                        </button>
                    </div>

                    {/* Expandable Filters */}
                    {showFilters && (
                        <div className="mb-6">
                            <div className="pb-4 flex flex-wrap gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Media Type</label>
                                    <select
                                        value={selectedType}
                                        onChange={(e) => setSelectedType(e.target.value)}
                                        className={`px-3 py-2 rounded-lg border text-sm ${
                                            darkMode 
                                                ? 'bg-gray-700 border-gray-600 text-white' 
                                                : 'bg-white border-gray-300 text-gray-800'
                                        }`}
                                    >
                                        {mediaTypes.map(type => (
                                            <option key={type} value={type}>
                                                {type === 'All' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl mr-4" />
                            <span className="text-xl">Loading media...</span>
                        </div>
                    ) : filteredMedia.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="mb-4">
                                <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-medium mb-2">
                                {searchTerm || selectedType !== 'All' ? 'No media found' : 'No media uploaded yet'}
                            </h3>
                            <p className="text-gray-500">
                                {searchTerm || selectedType !== 'All' 
                                    ? 'Try adjusting your search or filter criteria.' 
                                    : 'Upload your first media file to get started!'
                                }
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="mb-6 flex items-center justify-between">
                                <h3 className="text-lg font-medium">
                                    {filteredMedia.length} Media File{filteredMedia.length !== 1 ? 's' : ''} Found
                                </h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {filteredMedia.map((item) => (
                                    <div
                                        key={item._id}
                                        className={`rounded-lg border overflow-hidden ${
                                            darkMode 
                                                ? 'bg-gray-700/50 border-gray-600' 
                                                : 'bg-white border-gray-200'
                                        } hover:shadow-lg transition-shadow`}
                                    >
                                        {/* Media Preview */}
                                        <div className="relative h-48 overflow-hidden bg-gray-100 dark:bg-gray-800">
                                            {item.mediaType === 'image' ? (
                                                <img 
                                                    src={item.url} 
                                                    alt={item.title}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <FontAwesomeIcon 
                                                        icon={getMediaIcon(item.mediaType)} 
                                                        className="text-4xl text-gray-400" 
                                                    />
                                                </div>
                                            )}
                                            <div className="hidden w-full h-full items-center justify-center">
                                                <FontAwesomeIcon 
                                                    icon={getMediaIcon(item.mediaType)} 
                                                    className="text-4xl text-gray-400" 
                                                />
                                            </div>
                                            
                                            {/* Type Badge */}
                                            <div className="absolute top-2 right-2">
                                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${getTypeColor(item.mediaType)}`}>
                                                    {item.mediaType}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Media Info */}
                                        <div className="p-4">
                                            <h4 className="font-semibold mb-2 line-clamp-1">{item.title}</h4>
                                            {item.description && (
                                                <p className={`text-sm mb-3 line-clamp-2 ${
                                                    darkMode ? 'text-gray-300' : 'text-gray-600'
                                                }`}>
                                                    {item.description}
                                                </p>
                                            )}
                                            
                                            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
                                                <span className="flex items-center gap-1">
                                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                                    {formatDate(item.createdAt)}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <FontAwesomeIcon icon={faFileAlt} />
                                                    {formatFileSize(item.size)}
                                                </span>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => window.open(item.url, '_blank')}
                                                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium ${
                                                        darkMode 
                                                            ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                                                    } transition-colors flex items-center justify-center gap-1`}
                                                >
                                                    <FontAwesomeIcon icon={faEye} />
                                                    View
                                                </button>
                                                
                                                <button
                                                    onClick={() => handleDownload(item.url, item.title)}
                                                    className={`py-2 px-3 rounded-lg text-sm font-medium ${
                                                        darkMode 
                                                            ? 'bg-green-600 hover:bg-green-700 text-white' 
                                                            : 'bg-green-500 hover:bg-green-600 text-white'
                                                    } transition-colors flex items-center justify-center`}
                                                >
                                                    <FontAwesomeIcon icon={faDownload} />
                                                </button>
                                                
                                                <button
                                                    onClick={() => onEditMedia && onEditMedia(item)}
                                                    className={`py-2 px-3 rounded-lg text-sm font-medium ${
                                                        darkMode 
                                                            ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                                                            : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                                                    } transition-colors flex items-center justify-center`}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                
                                                <button
                                                    onClick={() => handleDeleteMedia(item._id)}
                                                    className={`py-2 px-3 rounded-lg text-sm font-medium ${
                                                        darkMode 
                                                            ? 'bg-red-600 hover:bg-red-700 text-white' 
                                                            : 'bg-red-500 hover:bg-red-600 text-white'
                                                    } transition-colors flex items-center justify-center`}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirmation.show && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className={`p-6 rounded-2xl shadow-xl max-w-md w-full border ${
                        darkMode ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 backdrop-blur-md border-gray-200'
                    }`}>
                        <h3 className="text-2xl font-semibold mb-2">Confirm Delete</h3>
                        <p className="mb-2">Are you sure you want to delete this media file?</p>
                        <p className="mb-6 font-semibold">&quot;{deleteConfirmation.mediaTitle}&quot;</p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setDeleteConfirmation({ show: false, mediaId: null, mediaTitle: '', isDeleting: false })}
                                className={`px-4 py-2 rounded ${
                                    darkMode ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'
                                }`}
                                disabled={deleteConfirmation.isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={deleteConfirmation.isDeleting}
                                className={`px-4 py-2 rounded text-white ${
                                    darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'
                                } disabled:bg-red-400 disabled:cursor-not-allowed flex items-center gap-2`}
                            >
                                {deleteConfirmation.isDeleting && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                                {deleteConfirmation.isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

MediaManagement.propTypes = {
    darkMode: PropTypes.bool.isRequired,
    onEditMedia: PropTypes.func
};

export default MediaManagement;
